import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from './EmptyState';
import { BarChart3, TrendingUp, DollarSign } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface PerformanceChartsProps {
  timeRange?: string;
  holdings: Holding[];
  loading: boolean;
}

interface Holding {
  id: string;
  holding_name: string;
  holding_type: string;
  initial_value: number;
  current_value: number;
  acquisition_date: string;
  created_at: string;
}

export const PerformanceCharts: React.FC<PerformanceChartsProps> = ({ timeRange = '12M', holdings, loading }) => {

  // Prepare portfolio value over time data
  // First, sort holdings chronologically by acquisition date
  const sortedHoldings = [...holdings].sort((a, b) => 
    new Date(a.acquisition_date).getTime() - new Date(b.acquisition_date).getTime()
  );

  // Create timeline data points for cumulative portfolio value
  const portfolioTimeline: { [key: string]: { value: number; initial: number; dateObj: Date; } } = {};
  
  sortedHoldings.forEach(holding => {
    const acquisitionDate = new Date(holding.acquisition_date);
    const dateKey = acquisitionDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    if (!portfolioTimeline[dateKey]) {
      portfolioTimeline[dateKey] = {
        value: 0,
        initial: 0,
        dateObj: acquisitionDate
      };
    }
    
    portfolioTimeline[dateKey].value += holding.current_value;
    portfolioTimeline[dateKey].initial += holding.initial_value;
  });

  // Convert to array and sort chronologically
  const portfolioData = Object.entries(portfolioTimeline)
    .map(([dateKey, data]) => ({
      date: data.dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: holdings.length > 10 ? '2-digit' : 'numeric' // Use 2-digit year for many holdings
      }),
      dateKey,
      value: data.value,
      initial: data.initial,
      dateObj: data.dateObj
    }))
    .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());

  // Calculate cumulative values chronologically
  let cumulativeValue = 0;
  let cumulativeInitial = 0;
  const cumulativeData = portfolioData.map(item => {
    cumulativeValue += item.value;
    cumulativeInitial += item.initial;
    return {
      ...item,
      totalValue: cumulativeValue,
      totalInitial: cumulativeInitial
    };
  });

  // Prepare holdings by type data
  const typeData = holdings.reduce((acc: any[], holding) => {
    const existingType = acc.find(item => item.type === holding.holding_type);
    if (existingType) {
      existingType.value += holding.current_value;
      existingType.count += 1;
    } else {
      acc.push({
        type: holding.holding_type,
        value: holding.current_value,
        count: 1
      });
    }
    return acc;
  }, []);

  // Sort by value (highest to lowest) and calculate total
  const sortedTypeData = typeData.sort((a, b) => b.value - a.value);
  const totalPortfolioValue = sortedTypeData.reduce((sum, item) => sum + item.value, 0);

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <EmptyState
              title="No Performance Data"
              description="Performance charts will appear here once you start tracking your partnerships and deals."
              icon={<BarChart3 className="w-8 h-8 text-muted-foreground" />}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Holdings by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <EmptyState
              title="No Holdings Data"
              description="Add holdings to see the breakdown by type and performance metrics."
              icon={<DollarSign className="w-8 h-8 text-muted-foreground" />}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Portfolio Value Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={cumulativeData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="date" 
                className="fill-muted-foreground text-xs"
              />
              <YAxis 
                className="fill-muted-foreground text-xs"
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value: number) => [`$${value.toLocaleString()}`, 'Value']}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
              />
              <Area
                type="monotone"
                dataKey="totalValue"
                stroke="hsl(var(--primary))"
                fill="hsl(var(--primary))"
                fillOpacity={0.2}
              />
              <Area
                type="monotone"
                dataKey="totalInitial"
                stroke="hsl(var(--muted-foreground))"
                fill="hsl(var(--muted))"
                fillOpacity={0.1}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Holdings by Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Holdings Breakdown Table */}
            <div className="lg:w-2/5">
              <div className="space-y-3">
                {sortedTypeData.map((item, index) => {
                  const percentage = totalPortfolioValue > 0 ? (item.value / totalPortfolioValue) * 100 : 0;
                  return (
                    <div key={item.type} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <div>
                          <div className="font-medium text-sm text-foreground">{item.type}</div>
                          <div className="text-xs text-muted-foreground">{item.count} holding{item.count !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-sm text-foreground">${item.value.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">{percentage.toFixed(1)}%</div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Total Row */}
                <div className="flex items-center justify-between p-3 rounded-lg border-2 border-primary/20 bg-primary/5 font-semibold">
                  <div className="text-foreground">Total Portfolio</div>
                  <div className="text-foreground">${totalPortfolioValue.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Pie Chart */}
            <div className="lg:w-3/5">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={sortedTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {sortedTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number, name: string, props: any) => {
                      const percentage = totalPortfolioValue > 0 ? (value / totalPortfolioValue) * 100 : 0;
                      const count = props.payload?.count || 0;
                      return [
                        <div key="tooltip" className="space-y-1">
                          <div className="font-semibold text-foreground">{props.payload?.type}</div>
                          <div className="text-foreground">${value.toLocaleString()}</div>
                          <div className="text-muted-foreground">{percentage.toFixed(1)}% of portfolio</div>
                          <div className="text-muted-foreground">{count} holding{count !== 1 ? 's' : ''}</div>
                        </div>,
                        ''
                      ];
                    }}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      padding: '12px'
                    }}
                    labelStyle={{ display: 'none' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};